/**
 * The guided path to a verified company: one step per profile section the
 * backend counts (`COMPLETION_SECTIONS` in company_service.py, same keys, same
 * order), then the request itself. Saving a step moves on to the next one that
 * is still open, so the guide always points at the next thing worth doing.
 */

/** Matches `_DESCRIPTION_MINIMUM` in company_service.py. */
export const DESCRIPTION_MINIMUM = 80;

export const GUIDE_STEPS = [
  {
    key: 'basics',
    title: 'Basics',
    description: 'Your industry and how big the team is.',
    icon: 'industry',
    fields: ['industry_id', 'company_size'],
  },
  {
    key: 'description',
    title: 'About',
    description: 'What your company does, and what interns will work on.',
    icon: 'fileText',
    fields: ['description'],
  },
  {
    key: 'logo',
    title: 'Logo',
    description: 'Shown on your profile and every internship you post.',
    icon: 'upload',
    fields: [],
  },
  {
    key: 'contact',
    title: 'Contact',
    description: 'How our team reaches you about the review. Never shown publicly.',
    icon: 'messages',
    fields: ['contact_email', 'contact_phone'],
  },
  {
    key: 'details',
    title: 'Details',
    description: 'Where you are based and the year you started.',
    icon: 'building',
    fields: ['location', 'founded_year'],
  },
  {
    key: 'links',
    title: 'Links',
    description: 'Your website or LinkedIn page, so we can check who you are.',
    icon: 'external',
    fields: ['website', 'linkedin_url'],
  },
  {
    key: 'submit',
    title: 'Submit for review',
    description: 'Send the request to our team.',
    icon: 'shieldCheck',
    fields: [],
  },
];

export const SUBMIT_STEP = 'submit';

/** Statuses in which the company still has to ask (or ask again). */
export const NEEDS_VERIFICATION = new Set(['UNVERIFIED', 'REJECTED']);

/** The section keys the completion breakdown reports as complete. */
export function completedKeys(completion) {
  return new Set(
    (completion?.sections ?? []).filter((section) => section.complete).map((s) => s.key),
  );
}

const PROFILE_STEPS = GUIDE_STEPS.filter((step) => step.key !== SUBMIT_STEP);

/** Where the guide opens: the first open section, or the request if none is. */
export function firstOpenStep(done) {
  return PROFILE_STEPS.find((step) => !done.has(step.key))?.key ?? SUBMIT_STEP;
}

/**
 * The step after `current` once it is saved or skipped: the next open section
 * after it, then any open one before it, then the request.
 */
export function nextStepAfter(current, done) {
  const index = PROFILE_STEPS.findIndex((step) => step.key === current);
  const after = PROFILE_STEPS.slice(index + 1).find((step) => !done.has(step.key));
  if (after) return after.key;
  // Coming from "skip", the step just left is still open; do not go back to it.
  const before = PROFILE_STEPS.slice(0, Math.max(index, 0)).find(
    (step) => !done.has(step.key) && step.key !== current,
  );
  return before?.key ?? SUBMIT_STEP;
}

const blank = (value) => value === undefined || value === null || String(value).trim() === '';

/**
 * What a step still needs before saving it completes the section. Mirrors
 * `completion_breakdown` so "Save" never leaves a step looking unfinished.
 */
export function missingFields(stepKey, values) {
  const need = (field, message) => (blank(values[field]) ? [{ field, message }] : []);
  switch (stepKey) {
    case 'basics':
      return [
        ...need('industry_id', 'Choose your industry'),
        ...need('company_size', 'Choose your company size'),
      ];
    case 'description': {
      const length = String(values.description ?? '').trim().length;
      return length >= DESCRIPTION_MINIMUM
        ? []
        : [
            {
              field: 'description',
              message: `Write at least ${DESCRIPTION_MINIMUM} characters (${length} so far)`,
            },
          ];
    }
    case 'contact':
      return [
        ...need('contact_email', 'Add a contact email'),
        ...need('contact_phone', 'Add a contact phone number'),
      ];
    case 'details':
      return [
        ...need('location', 'Add your location'),
        ...need('founded_year', 'Add the year you were founded'),
      ];
    case 'links':
      return blank(values.website) && blank(values.linkedin_url)
        ? [{ field: 'website', message: 'Add your website or your LinkedIn page' }]
        : [];
    default:
      return [];
  }
}

/**
 * The guide opens by itself once per browser session, right after sign-in.
 * Storage can be missing or throw (private windows); then it simply opens.
 */
const OPENED_KEY = 'jw.company.verification-guide-opened';

// Also kept in memory, so a browser without session storage still opens the
// guide once per page load rather than on every visit to the dashboard.
let openedInMemory = null;

export function guideAlreadyOpened(userId) {
  if (openedInMemory === String(userId)) return true;
  try {
    return window.sessionStorage.getItem(OPENED_KEY) === String(userId);
  } catch {
    return false;
  }
}

export function markGuideOpened(userId) {
  openedInMemory = String(userId);
  try {
    window.sessionStorage.setItem(OPENED_KEY, String(userId));
  } catch {
    // The in-memory flag covers this page load.
  }
}
